import { useEffect, useState, type RefObject } from "react";

type UseScrollStateParams = {
  ref?: RefObject<HTMLElement | null>;
  threshold?: number;
  hideOnScrollDown?: boolean;
  enabled?: boolean;
};

export type ScrollState = {
  isScrolled: boolean;
  isHidden: boolean;
};

const DIRECTION_DELTA = 4;

const SCROLLABLE = /(auto|scroll|overlay)/;

export const getScrollParent = ({ element }: { element: HTMLElement | null | undefined }): HTMLElement | Window => {
  if (typeof window === "undefined") return globalThis as unknown as Window;
  let node = element?.parentElement ?? null;
  while (node && node !== document.body && node !== document.documentElement) {
    if (SCROLLABLE.test(getComputedStyle(node).overflowY)) return node;
    node = node.parentElement;
  }
  return window;
};

const readScrollY = ({ target }: { target: HTMLElement | Window }): number =>
  target === window ? window.scrollY : (target as HTMLElement).scrollTop;

export const useScrollState = ({
  ref,
  threshold = 48,
  hideOnScrollDown = false,
  enabled = true,
}: UseScrollStateParams = {}): ScrollState => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      setIsScrolled(false);
      setIsHidden(false);
      return;
    }

    const target = getScrollParent({ element: ref?.current });

    const reducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const canHide = hideOnScrollDown && !reducedMotion;

    const raf =
      typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame.bind(window)
        : (callback: FrameRequestCallback) => {
            callback(0);
            return 0;
          };

    let lastY = readScrollY({ target });
    let ticking = false;

    const update = () => {
      const y = readScrollY({ target });
      setIsScrolled(y > threshold);

      if (canHide) {
        const delta = y - lastY;
        if (Math.abs(delta) >= DIRECTION_DELTA) {
          setIsHidden(delta > 0 && y > threshold);
        } else if (y <= threshold) {
          setIsHidden(false);
        }
      } else {
        setIsHidden(false);
      }

      lastY = y;
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      raf(update);
    };

    update();
    target.addEventListener("scroll", onScroll, { passive: true });

    return () => target.removeEventListener("scroll", onScroll);
  }, [ref, enabled, threshold, hideOnScrollDown]);

  return { isScrolled, isHidden };
};
